import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Box, Typography, TextField, Grid, Button, IconButton, CircularProgress, useMediaQuery, Skeleton } from '@mui/material';
import { FaEye, FaEyeSlash, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, FaFacebook, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import ReCAPTCHA from "react-google-recaptcha";
import 'react-toastify/dist/ReactToastify.css';
import { ShimmerSimpleGallery } from "react-shimmer-effects";
import Loginimage from "../../../../assets/learnersSignin.webp";
import Headrepage from "../../../mainpage/Headerpages/learnersHederpage";
import { LearnersVerifyCaptcha, LearnersLogin } from '../../../../helper/base_url';
import { isTokenExpired } from "../../../../utils/tokenUtils";
import Footer from "../../../mainpage/learnersFooter";
import useNetworkStatus from "../../../../helper/NetworkStatus";

const regex = {
  email: /^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i,
  // email: /^(?!.*\.\.)([A-Za-z0-9](?:[A-Za-z0-9._%+-]{0,62}[A-Za-z0-9])?)@[A-Za-z][A-Za-z0-9-]*(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/,
  password: /^(?=.*\d)(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[a-z])(?!.*\s).{8,}$/,
};

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [data, setData] = useState({ email: "", password: "", role: "student" });
  const recaptchaRef = useRef(null);
  const isMobile = useMediaQuery('(max-inline-size:600px)');
  const isTablet = useMediaQuery('(min-inline-size:601px) and (max-inline-size:960px)');
  const { isOnline } = useNetworkStatus();
  const [errors, setErrors] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  const location = useLocation();
  const [captchaStatus, setCaptchaStatus] = useState(null);

  useEffect(() => {
  // 1. Check if SSO token exists in URL
  const urlParams = new URLSearchParams(location.search);
  const ssoToken = urlParams.get("ssoToken");

  if (ssoToken) {
    localStorage.setItem("token", ssoToken);
    window.history.replaceState({}, document.title, location.pathname);
    setTimeout(() => {
        navigate("/learners/dashboardpage");
      }, 100); // short delay to let React render
      return;
  }

  // 2. Otherwise, check existing token in storage
  const token = localStorage.getItem("token");
  if (token) {
    if (isTokenExpired(token)) {
      localStorage.removeItem("token");
      toast.warning("Session expired. Please log in again.");
      setCheckingToken(false);
    } else {
      setCheckingToken(false);
      setTimeout(() => {
        navigate("/learners/dashboardpage");
      }, 1000);
    }
  } else {
    setCheckingToken(false);
  }
}, [navigate, location]);

  useEffect(() => {
    const error = new URLSearchParams(location.search).get("error");
    if (error) {
      toast.error(
        error === "registration_required"
          ? "User not found. Please register first."
          : "An unexpected error occurred."
      );
    }
  }, [location.search]);

  const validateField = (name, value) => {
    if (!value.trim())
      return `${name[0].toUpperCase() + name.slice(1)} is required.`;
    if (name === "email") {
      const emailValue = value.toLowerCase();
      if (!regex.email.test(emailValue)) return "Invalid email format.";
    }
    if (name === "password" && !regex.password.test(value))
      return "Invalid password format.";
    return "";
  };

  const handleChange = (e) => {
      const { name, value } = e.target;
      //Convert email to lowercase automatically
      const updatedValue = name === "email" ? value.toLowerCase() : value;
      setData((prev) => ({ ...prev, [name]: updatedValue }));

      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, updatedValue)
      }));
    };


    const handleCaptchaChange = async (token) => {
    setCaptchaToken(token);
    try {
      const res = await axios.post(LearnersVerifyCaptcha, { captchaToken: token });
      if (res.status === 200) {
        setCaptchaVerified(true);
        setCaptchaStatus("success");
      }
    } catch {
      setCaptchaVerified(false);
      setCaptchaStatus("failed");
      recaptchaRef.current?.reset();
      setCaptchaToken("");
    }
  };

   const submitHandler = async (e) => {
    e.preventDefault();

    const emailError = validateField("email", data.email);
    const passwordError = validateField("password", data.password);
    setErrors({ email: emailError, password: passwordError });

    if (emailError || passwordError) {
      toast.error("Please fix errors before submitting.");
      return;
    }

    if (!captchaToken) {
      toast.error("Please complete the reCAPTCHA.");
      return;
    }

    setLoading(true);
    const toastId = toast.loading("Logging in...");

    try {
      const response = await axios.post(LearnersLogin, data);
      const { token, message } = response.data;

      toast.update(toastId, {
        render: message || "Login successful!",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });

      localStorage.setItem("token", token);

      setTimeout(() => navigate("/learners/dashboardpage"), 1000);
    } catch (error) {
      const msg = error.response?.data?.message || "Login failed.";
      toast.update(toastId, {
        render: msg,
        type: "error",
        isLoading: false,
        autoClose: 1500,
      });
      recaptchaRef.current?.reset();
      setCaptchaToken("");
    } finally {
      setLoading(false);
    }
  };

  // Show shimmer while checking token
  if (checkingToken) {
    return (
      <Box sx={{
        height : "100vh", width : "100%", display: "flex",
        justifyContent: "center", alignItems: "center",
        bgcolor: "#f5f7fa", padding: "20px"
      }}>
        <Box sx={{
          width : { xs: "90%", sm: "70%", md: "50%", lg: "40%" },
          padding: 3, borderRadius: 3, boxShadow: 3, bgcolor: "#ffffff"
        }}>
          <Skeleton variant="text" height={50} width="80%" sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
          <Skeleton variant="rounded" height={40} width="100%" sx={{ mb: 3 }} />
          <Skeleton variant="text" width="60%" />
        </Box>
      </Box>
    );
  }

  return (
    <div className="students-flex">
      <Headrepage />
      {isOnline ? (
      <div className="homepagecolores">
      <div style={{ width : "100%", overflowX: "hidden", overflowY: "auto"}}>
        <ToastContainer position="top-center" />
       <Grid container justifyContent="center" alignItems="center" sx={{ p: { xs: "20px 20px", md: "30px 20px" }, gap: 5 }} >
          <Grid item xs={12} sm={8} md={6} lg={4}>
            <Typography variant="h5" align="center" gutterBottom sx={{ color: "#043885" }}>
              Log in to your account
            </Typography>

              <form onSubmit={submitHandler}>
                <TextField label="Email" type="email" name="email" fullWidth margin="normal"
                  value={data.email} onChange={handleChange} required disabled={loading} 
                  error={!!errors.email}
                  helperText={errors.email}
                  placeholder="Enter your email address"
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 700, color: "black", "& .MuiFormLabel-asterisk": { color: data.email ? "blue" : "red", } },
                  }}
                  sx={{
                    backgroundColor: "white",
                  }}
                  />
                <Box position="relative">
                  <TextField label="Password" type={showPassword ? "text" : "password"} name="password"
                    fullWidth margin="normal" value={data.password} onChange={handleChange} required disabled={loading}
                    error={!!errors.password}
                    helperText={errors.password}
                    placeholder="Enter your password"
                    InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 700, color: "black", "& .MuiFormLabel-asterisk": { color: data.password ? "blue" : "red", } },
                  }}
                  sx={{
                  backgroundColor: "white",
                  }}
                  />
                  <IconButton onClick={() => setShowPassword(prev => !prev)}
                    style={{ position: "absolute", right : 10, top : "28%" }}>
                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                  </IconButton>
                </Box>
                <div style={{ margin: "20px 0px" }}>
                  <ReCAPTCHA
                    sitekey="6Le83KcqAAAAAInqCfWquiIGanUjpu2RxTMgKwuJ"
                    onChange={handleCaptchaChange}
                    ref={recaptchaRef}
                  />
                  {captchaStatus === "success" && (
                    <p style={{ color: "green", fontSize: "14px", marginTop : "8px", display: "flex", alignItems: "center", gap: "5px" }}>
                      <FaCheckCircle /> Verified successfully
                    </p>
                  )}

                  {captchaStatus === "failed" && (
                    <p style={{ color: "red", fontSize: "14px", marginTop : "8px", display: "flex", alignItems: "center", gap: "5px" }}>
                      <FaTimesCircle /> Verification failed. Try again.
                    </p>
                  )}
                </div>
                 <Typography sx={{ marginTop : "10px" }}>
                  <Link to="/learners/forgot-password">Forgot Password</Link>
                </Typography>
                <Button type="submit" variant="contained" color="primary" fullWidth
                  style={{ marginTop : 10, backgroundColor: "rgb(27, 110, 244)" }} disabled={!captchaVerified || loading}>
                  {loading ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>
              </form>
              <>
                <Typography align="center" style={{ marginTop : 15 }}>
                  Don't have an account?{" "}
                  <Link to="/learners/signup">Sign up</Link>
                </Typography>
              </>
          </Grid>
          {!isMobile && !isTablet && (
            <Grid item xs={12} sm={8} md={6} lg={4} display="flex" justifyContent="center" sx={{ position: "relative" }}>
                <Box component="img" src={Loginimage} alt="Signup"
                  sx={{
                    width : "100%",
                    maxWidth : 650,
                    height : "550px",
                    borderRadius: 3,
                  }}
                />
              </Grid>
          )}
        </Grid>
        </div>
        <div className="homepage-footers-images">
              <Footer />
            <footer className="homepage-footers">
            <hr className='homepage-footer-hr'/>
            <div className="learners-footers-seokeywords">
            </div>
              <div className="footer-social">
              <div className="homepage-footer-copyright">
                <p>© Huebits[2025].All Rights Reserved.</p>
              </div>
              <div className='footer-social-icons'>
                <Link to="https://www.linkedin.com/company/huebits/mycompany" target="_blank" rel="noopener noreferrer"><FaLinkedin  className="social-icon"/></Link>
                <Link to="https://x.com/HuebitsTech" target="_blank" rel="noopener noreferrer"><FaTwitter className="social-icon" /></Link>
                <Link to="https://www.instagram.com/huebits_tech/" target="_blank" rel="noopener noreferrer"><FaInstagram className="social-icon" /></Link>
                <Link to="https://www.youtube.com/@HuebitsTech" target="_blank" rel="noopener noreferrer"><FaYoutube className="social-icon" /></Link>
                <Link to="https://www.facebook.com/profile.php?id=61571753805069" target="_blank" rel="noopener noreferrer"><FaFacebook className="social-icon" /></Link>
            </div>
            </div>
            </footer>
           </div>
          </div>
          ) : (
              <div>
                  <ShimmerSimpleGallery card imageHeight={180} row={3} col={3} gap={20} />
              </div>
          )}
      </div>
  );
}

export default Login;